/**
 * Utilitários puros para validação cronológica de horários no RDO.
 * Todas as funções retornam { valid: boolean, message: string|null }.
 * Campos vazios/null/undefined NÃO disparam validação (permite rascunho parcial).
 */

// Converte HH:MM para minutos do dia (0-1439). null se inválido.
export const timeToMinutes = (t) => {
  if (!t || typeof t !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
};

const isFilled = (v) => v !== null && v !== undefined && v !== "";

/**
 * Valida que timeB >= timeA (B deve vir igual ou depois de A).
 * Só valida se AMBOS estão preenchidos.
 */
export const timeAtLeast = (timeA, timeB, labelA, labelB) => {
  if (!isFilled(timeA) || !isFilled(timeB)) return { valid: true, message: null };
  const a = timeToMinutes(timeA);
  const b = timeToMinutes(timeB);
  if (a === null || b === null) return { valid: true, message: null };
  if (b < a) {
    return {
      valid: false,
      message: `${labelB} (${timeB}) deve ser maior ou igual a ${labelA} (${timeA})`,
    };
  }
  return { valid: true, message: null };
};

/**
 * Valida uma cadeia de horários onde cada item ≥ anterior.
 * chain: array de [fieldKey, value, label]
 * Retorna objeto de errors {fieldKey: message} contendo SOMENTE chaves com violação.
 */
export const validateTimeChain = (chain) => {
  const errors = {};
  let prevIdx = -1;
  for (let i = 0; i < chain.length; i++) {
    const [, value] = chain[i];
    if (!isFilled(value) || timeToMinutes(value) === null) continue;
    if (prevIdx >= 0) {
      const [, prevValue, prevLabel] = chain[prevIdx];
      const [keyB, valueB, labelB] = chain[i];
      const r = timeAtLeast(prevValue, valueB, prevLabel, labelB);
      if (!r.valid) errors[keyB] = r.message;
    }
    prevIdx = i;
  }
  return errors;
};

/**
 * Valida cada entry de atividade individualmente: end_time >= start_time.
 * Retorna { [`${period}_entry_${idx}_end_time`]: message } para cada violação.
 */
export const validateEntryTimes = (entries, period) => {
  const errors = {};
  (entries || []).forEach((e, idx) => {
    const r = timeAtLeast(e?.start_time, e?.end_time, "Início", "Fim");
    if (!r.valid) errors[`${period}_entry_${idx}_end_time`] = r.message;
  });
  return errors;
};

/**
 * Valida que cada entry está dentro do período [periodStart, periodEnd].
 * Apenas valida se a borda relevante (start ou end) e a entry estão preenchidas.
 */
export const validateEntriesWithinPeriod = (entries, periodStart, periodEnd, period, periodStartLabel, periodEndLabel) => {
  const errors = {};
  (entries || []).forEach((e, idx) => {
    if (isFilled(periodStart) && isFilled(e?.start_time)) {
      const r = timeAtLeast(periodStart, e.start_time, periodStartLabel, `Início da ocorrência #${idx + 1}`);
      if (!r.valid) errors[`${period}_entry_${idx}_start_time`] = r.message;
    }
    if (isFilled(periodEnd) && isFilled(e?.end_time)) {
      const r = timeAtLeast(e.end_time, periodEnd, `Fim da ocorrência #${idx + 1}`, periodEndLabel);
      if (!r.valid) errors[`${period}_entry_${idx}_end_time`] = r.message;
    }
  });
  return errors;
};

/**
 * Valida que entries do mesmo período NÃO se sobrepõem (1 robô).
 * Sobreposição = um intervalo [start, end] de uma entry intersecta o de outra.
 * Retorna { [`${period}_entry_${idx}_overlap`]: message } para cada conflito detectado.
 */
export const validateNoOverlap = (entries, period) => {
  const errors = {};
  const valid = (entries || [])
    .map((e, idx) => ({
      idx,
      start: timeToMinutes(e?.start_time),
      end: timeToMinutes(e?.end_time),
    }))
    .filter((x) => x.start !== null && x.end !== null && x.end >= x.start);

  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      const a = valid[i];
      const b = valid[j];
      // Sobreposição: max(start) < min(end)
      if (Math.max(a.start, b.start) < Math.min(a.end, b.end)) {
        const key = `${period}_entry_${b.idx}_overlap`;
        if (!errors[key]) {
          errors[key] = `Esta ocorrência sobrepõe com a Ocorrência #${a.idx + 1} (operamos com 1 robô — não pode haver atividades simultâneas)`;
        }
      }
    }
  }
  return errors;
};

/**
 * Valida cadeia macro do dia inteiro (cross-aba).
 * Usado APENAS no submit final.
 * Retorna { valid, errors: { tabId: { fieldKey: message } } }
 */
export const validateGlobalChain = (formData) => {
  const errs = { admin: {}, lunch: {}, closing: {} };

  // Pegar última end_time da manhã
  const morningEntries = formData.morning_activity_entries || [];
  const lastMorningEnd = morningEntries
    .map((e) => timeToMinutes(e?.end_time))
    .filter((m) => m !== null)
    .reduce((max, m) => (m > max ? m : max), -1);

  // Pegar última end_time da tarde
  const afternoonEntries = formData.afternoon_activity_entries || [];
  const lastAfternoonEnd = afternoonEntries
    .map((e) => timeToMinutes(e?.end_time))
    .filter((m) => m !== null)
    .reduce((max, m) => (m > max ? m : max), -1);

  // 1) Manhã ≤ Saída almoço
  if (isFilled(formData.lunch_departure_time) && lastMorningEnd > 0) {
    const dep = timeToMinutes(formData.lunch_departure_time);
    if (dep !== null && lastMorningEnd > dep) {
      const h = String(Math.floor(lastMorningEnd / 60)).padStart(2, "0");
      const m = String(lastMorningEnd % 60).padStart(2, "0");
      errs.lunch.lunch_departure_time =
        `Saída para almoço (${formData.lunch_departure_time}) deve ser ≥ Fim da última atividade da manhã (${h}:${m})`;
    }
  }

  // 2) Volta almoço ≤ início tarde (primeira entry)
  const firstAfternoonStart = afternoonEntries
    .map((e) => timeToMinutes(e?.start_time))
    .filter((m) => m !== null)
    .reduce((min, m) => (m < min ? m : min), 24 * 60 + 1);
  if (isFilled(formData.lunch_return_time) && firstAfternoonStart < 24 * 60 + 1) {
    const ret = timeToMinutes(formData.lunch_return_time);
    if (ret !== null && firstAfternoonStart < ret) {
      const h = String(Math.floor(firstAfternoonStart / 60)).padStart(2, "0");
      const m = String(firstAfternoonStart % 60).padStart(2, "0");
      errs.lunch.lunch_return_time =
        `Volta do almoço (${formData.lunch_return_time}) deve ser ≤ Início da primeira atividade da tarde (${h}:${m})`;
    }
  }

  // 3) Encerramento ≥ última atividade da tarde
  if (isFilled(formData.daily_end_time) && lastAfternoonEnd > 0) {
    const end = timeToMinutes(formData.daily_end_time);
    if (end !== null && end < lastAfternoonEnd) {
      const h = String(Math.floor(lastAfternoonEnd / 60)).padStart(2, "0");
      const m = String(lastAfternoonEnd % 60).padStart(2, "0");
      errs.closing.daily_end_time =
        `Encerramento do dia (${formData.daily_end_time}) deve ser ≥ Fim da última atividade da tarde (${h}:${m})`;
    }
  }

  // Limpar tabs sem erros
  Object.keys(errs).forEach((k) => {
    if (Object.keys(errs[k]).length === 0) delete errs[k];
  });
  const valid = Object.keys(errs).length === 0;
  return { valid, errors: errs };
};
